import { TextSection as TextSectionType } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/composer.pb';
import { EmailAssistantModal } from '@frontend/email-assistant-modal';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { RTEditor, useModalControl } from '@frontend/design-system';
import { useContentComposer } from '../../content-composer/content-composer-provider';
import { useComposerSection } from '../../content-composer/hooks/use-composer-section';
import { SectionBase } from '../../content-composer/section-base/section-base';
import type { ComposerSection } from '../../content-composer/types';
import { getFrameStyle } from '../utils/get-frame-style';
import { RTERoot } from './rte-root';
import { TextOutput } from './text-output';
import { TextSettings } from './text-settings';
import { TextTool } from './text-tool';

export const TextSection: ComposerSection<TextSectionType> = ({ frame, text }) => {
  const { t } = useTranslation('content-composer');
  const { composerMode, promptProps } = useContentComposer();
  const { isSelected, replaceSection } = useComposerSection<TextSectionType>();
  const { modalProps, triggerProps } = useModalControl();

  return (
    <SectionBase
      className='text-section'
      css={{
        '&.text-section .editor-root': getFrameStyle(frame),
        '&.text-section .editor-container': {
          background: 'transparent',
          margin: 0,
          position: 'initial',
        },
        '&.text-section .editor-inner': {
          background: 'transparent',
        },
        '&.text-section .editor-scroller': {
          minHeight: 'unset',
          paddingBottom: '4px',
          resize: 'none',
        },
        '&.text-section .editor-placeholder': {
          left: '0',
          top: '0',
        },
        '&.text-section .editor-input': {
          minHeight: theme.spacing(2),
          padding: '0',
        },
        '&.text-section .editor-paragraph': {
          lineHeight: theme.font.lineHeight,
          margin: 0,
        },
        '&.text-section .toolbar-container': {
          flexWrap: 'nowrap',
          margin: 0,
          padding: 0,
          width: 'initial',
        },
        '&.text-section .toolbar-container button': {
          boxShadow: theme.shadows.light,
          color: theme.colors.neutral70,
          fontSize: theme.font.size.medium,
          fontWeight: theme.font.weight.bold,
          height: 'initial',
          minHeight: theme.spacing(4),
          minWidth: theme.spacing(4),
          transition: 'all 250ms ease',
          ':hover, :focus': {
            background: theme.colors.neutral5,
            boxShadow: theme.shadows.floating,
            color: theme.colors.black,
          },
        },
      }}
      openAssistant={composerMode !== 'feedback' ? triggerProps.onClick : undefined}
    >
      <RTERoot>
        <RTEditor.Editor placeholder={isSelected ? t('Enter text here...') : ''} />
      </RTERoot>
      <EmailAssistantModal
        modalProps={modalProps}
        onComplete={(value: string) => replaceSection({ text: value })}
        previousText={text}
        promptProps={promptProps}
        type='text'
      />
    </SectionBase>
  );
};

TextSection.craft = {
  custom: {
    controlOutputRender: true,
  },
  props: {
    frame: {
      alignment: 'center',
      backgroundColor: theme.colors.neutral5.toUpperCase(),
      paddingBottom: theme.spacing(3),
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      paddingTop: theme.spacing(3),
    },
    sectionType: 'TextSection',
    text: '',
  },
  related: {
    output: TextOutput,
    settings: TextSettings,
    tool: TextTool,
  },
};
